// The public booking API (src/routes/booking.js). No login, so none of the CRM
// client's token handling: a 401 here would be a bug, not an expired session.

export class BookingApiError extends Error {
  constructor(status, body) {
    super((body && body.error) || "Something went wrong. Please try again.");
    this.status = status;
    this.code = body && body.code;
    this.hostEmail = body && body.host_email;
  }
}

async function call(path, { method = "GET", body } = {}) {
  let res;
  try {
    res = await fetch(`/api/book${path}`, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Offline or the server is down: the same screen as the calendar being down.
    throw new BookingApiError(503, { code: "network" });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new BookingApiError(res.status, data);
  return data;
}

const q = (params) =>
  Object.entries(params)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

// An in-person page that knows where sends the place in a POST body, so an
// address never sits in a URL (and so in an access log).
export const getSlots = ({ tz, invite, handoff, preview, as, type, ref, utm, place }) =>
  place && place.at === "theirs"
    ? call(`/slots?${q({ tz, c: invite, lp: handoff, p: preview, as, type, ref, utm })}`, { method: "POST", body: { place } })
    : call(`/slots?${q({ tz, c: invite, lp: handoff, p: preview, as, type, ref, utm, at: place && place.at })}`);
// The CRM's Page-tab preview: a sample success screen. Reads only.
export const getPreviewSuccess = ({ preview, tz, as, type }) => call(`/preview/success?${q({ p: preview, tz, as, type })}`);
export const getRead = ({ id, preview }) => call(`/reads/${encodeURIComponent(id)}?${q({ p: preview })}`);
export const book = (body) => call("", { method: "POST", body });
export const getManage = ({ token, tz }) => call(`/manage/${encodeURIComponent(token)}?${q({ tz })}`);
export const reschedule = ({ token, start, tz }) =>
  call(`/manage/${encodeURIComponent(token)}/reschedule`, { method: "POST", body: { start, tz } });
export const updateAgenda = ({ token, ...fields }) =>
  call(`/manage/${encodeURIComponent(token)}/agenda`, { method: "POST", body: fields });
export const addGuest = ({ token, email }) =>
  call(`/manage/${encodeURIComponent(token)}/guests`, { method: "POST", body: { email } });
export const removeGuest = ({ token, email }) =>
  call(`/manage/${encodeURIComponent(token)}/guests/remove`, { method: "POST", body: { email } });
export const cancel = ({ token, checkIn }) =>
  call(`/manage/${encodeURIComponent(token)}/cancel`, { method: "POST", body: { check_in: checkIn || null } });

/*
 * While the page is open and in front, a beat every 15 seconds, so the CRM can
 * show who is on it right now (src/analytics/presence.js); a goodbye as it
 * closes. `token` is the page data's `beat`, null for previews. Returns a stop.
 */
export function startBeat(token) {
  if (!token || typeof document === "undefined") return () => {};
  const send = (ev) => {
    const body = JSON.stringify(ev ? { beat: token, ev } : { beat: token });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon("/api/book/ping", new Blob([body], { type: "text/plain" }))) return;
    } catch {}
    fetch("/api/book/ping", { method: "POST", body, keepalive: true, headers: { "Content-Type": "text/plain" } }).catch(() => {});
  };
  let timer = null;
  const on = () => {
    if (timer) return;
    send();
    timer = setInterval(() => send(), 15000);
  };
  // Out of view or closing: stamp the moment once, so the visit ends when they left, not at the last beat.
  const off = () => {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
    send("hide");
  };
  const onVisibility = () => (document.visibilityState === "visible" ? on() : off());
  const onHide = () => {
    off();
    send("leave");
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onHide);
  if (document.visibilityState === "visible") on();
  return () => {
    off();
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onHide);
  };
}
